import { first, take } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../../../models/account';
import { CmsPage, Region, Suburb } from '../../../../models/content';
import { AccountService } from '../../../../services/account.service';
import { ModalService } from '../../../../services/modal.service';

/**
 * Website content helpers the Suburbs workspace shares with the content editors: reading collections from the
 * account's content, finding a suburb's or region's designed page, and flagging a region as having unpublished
 * suburb changes. Moved here from the old Suburbs list (#256), which the workspace replaces.
 */

/** A content collection as a list, live records only, in display order. */
export function contentList<T extends { _meta?: { index?: number; deleted?: boolean } }>(
  data: any
): T[] {
  if (!data) return [];
  const list = (Array.isArray(data) ? data : Object.values(data)).filter(
    (x: any) => x && x?._meta?.deleted !== true
  );
  return _.sortBy(list as T[], (r) => r?._meta?.index ?? Number.MAX_SAFE_INTEGER);
}

export function accountSnapshot(accountService: AccountService): Account | undefined {
  let s: Account | undefined;
  accountService.account$.pipe(first()).subscribe({ next: (a) => (s = a) });
  return s;
}

const canon = (v: string) =>
  String(v || '')
    .trim()
    .replace(/\/+$/, '') || '/';

function pages(account: Account | undefined): CmsPage[] {
  const raw: any = account?.data?.content?.pages;
  return (Array.isArray(raw) ? raw : Object.values(raw ?? {})).filter(
    (p: any) => p && typeof p === 'object'
  );
}

export function regionHubPath(region: Region): string {
  return `/${(region.state || 'nsw').toLowerCase()}/${region.slug}`;
}

/** A suburb's designed page: the CMS record at its funeral-services address, if any. */
export function suburbDesignedPage(
  account: Account | undefined,
  suburb: Suburb,
  regions: Region[]
): CmsPage | undefined {
  const region = regions.find((r) => r._meta.id === suburb.regionId);
  if (!region?.slug || !suburb.slug) return undefined;
  const path = `${regionHubPath(region)}/funeral-services/${suburb.slug}`;
  return pages(account).find((p) => canon(p.path) === path);
}

export function shortId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function nextIndex(existing: Array<{ _meta?: { index?: number } }>): number {
  return (
    existing.reduce((acc, s) => {
      const i = s?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10) + 10
  );
}

/**
 * Flag the owning region as having unpublished suburb changes.
 *
 * Suburbs have no publish action of their own: they ship with their region (picaluna-v3 merges them per region).
 * So a suburb edit changes nothing on the site until someone publishes the region, and nothing else would say so.
 *
 * Best-effort: a failure must never block the suburb save that already succeeded. Only writes when the flag
 * changes, and refetches the region first: the per-item upsert is a full replace, so writing a stale copy would
 * revert fields another content manager changed since this page loaded.
 */
export function markRegionSuburbsChanged(
  accountService: AccountService,
  modalService: ModalService,
  regionId: string
): void {
  if (!regionId) return;
  const region = contentList<Region>(accountSnapshot(accountService)?.data?.content?.regions).find(
    (r) => r?._meta?.id === regionId
  );
  if (!region || region.suburbsChangedAt) return;
  accountService
    .get(true)
    .pipe(first())
    .subscribe({
      next: () => {
        const fresh = contentList<Region>(
          accountSnapshot(accountService)?.data?.content?.regions
        ).find((r) => r?._meta?.id === regionId);
        if (!fresh || fresh.suburbsChangedAt) return;
        const stamped = { ...fresh, suburbsChangedAt: new Date().toISOString() };
        accountService
          .patchContentItem('regions', stamped)
          .pipe(take(1))
          .subscribe({
            next: () => (region.suburbsChangedAt = stamped.suburbsChangedAt),
            error: (err) => modalService.error(err?.error?.error || err),
          });
      },
      error: () => {
        // Refetch failed: leave the flag unset so the next suburb edit retries.
      },
    });
}

/** Put a saved record into the cached account's content collection (the server already has it). */
export function upsertCached(
  accountService: AccountService,
  kind: 'suburbs' | 'regions' | 'pages' | 'pageTemplates',
  item: any
): void {
  const account = accountSnapshot(accountService);
  if (!account) return;
  if (!account.data.content) account.data.content = {};
  const raw = account.data.content[kind];
  if (Array.isArray(raw)) {
    const i = raw.findIndex((x: any) => x?._meta?.id === item._meta.id);
    if (i >= 0) raw[i] = item;
    else raw.push(item);
  } else {
    account.data.content[kind] = { ...(raw ?? {}), [item._meta.id]: item };
  }
}

export function removeCached(
  accountService: AccountService,
  kind: 'suburbs' | 'regions' | 'pages' | 'pageTemplates',
  id: string
): void {
  const account = accountSnapshot(accountService);
  const raw = account?.data?.content?.[kind];
  if (Array.isArray(raw)) account!.data.content[kind] = raw.filter((x: any) => x?._meta?.id !== id);
  else if (raw && typeof raw === 'object') delete raw[id];
}
